import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Link,
  alpha
} from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from "@mui/lab";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import EventIcon from "@mui/icons-material/Event";
import NotesIcon from "@mui/icons-material/Notes";
import PersonIcon from "@mui/icons-material/Person";
import BlockIcon from "@mui/icons-material/Block";
import DescriptionIcon from "@mui/icons-material/Description";
import CategoryIcon from "@mui/icons-material/Category";
import SmsIcon from "@mui/icons-material/Sms";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import CelebrationIcon from "@mui/icons-material/Celebration";
import axios from "axios";
import { PastInteraction } from "../types/interactions";
import { formatDate } from "../../../../utils";
import { fTime } from "../../../utils/formatTime";

const momentumUrl = import.meta.env.VITE_MOMENTUM_URL;

interface PastInteractionsModalProps {
  open: boolean;
  onClose: () => void;
  contactId: number;
  orderNumber: string;
  contactName: string;
  statusFilter: string;
}

export const PastInteractionsModal = ({
  open,
  onClose,
  contactId,
  orderNumber,
  contactName,
  statusFilter
}: PastInteractionsModalProps) => {
  const theme = useTheme();
  const [interactions, setInteractions] = useState<PastInteraction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPastInteractions = async () => {
      if (!open || !contactId) return;

      setIsLoading(true);
      setError(null);

      try {
        const { data } = await axios.get<PastInteraction[]>(
          "/api/clientphonebook/contactInteractions/getPastInteractions",
          {
            params: {
              contactId
            }
          }
        );

        setInteractions(data);
      } catch (err) {
        setError("Failed to load past interactions");
        console.error("Error fetching past interactions:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPastInteractions();
  }, [open, contactId]);

  const getInteractionIcon = (typeName: string | null) => {
    switch (typeName?.toLowerCase()) {
      case "phone call":
        return <PhoneIcon />;
      case "text":
        return <SmsIcon />;
      case "email":
        return <EmailIcon />;
      case "event":
        return <EventIcon />;
      case "in person office visit":
        return <MeetingRoomIcon />;
      case "in person encounter":
        return <Diversity3Icon />;
      case "1031 candy":
        return <CardGiftcardIcon />;
      case "voice mail":
        return <RecordVoiceOverIcon />;
      case "simcha":
        return <CelebrationIcon />;
      case "skipped":
        return <BlockIcon />;
      default:
        return <NotesIcon />;
    }
  };

  const getInteractionColor = (typeName: string | null) => {
    switch (typeName?.toLowerCase()) {
      case "phone call":
        return theme.palette.info.main;
      case "text":
        return theme.palette.info.light;
      case "email":
        return theme.palette.success.main;
      case "event":
        return theme.palette.secondary.main;
      case "in person office visit":
        return theme.palette.primary.main;
      case "in person encounter":
        return theme.palette.primary.light;
      case "1031 candy":
        return "#ff69b4";
      case "voice mail":
        return theme.palette.info.dark;
      case "simcha":
        return "#FFD700";
      case "skipped":
        return theme.palette.warning.main;
      default:
        return theme.palette.grey[500];
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2
        }
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Past Interactions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {contactName}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ py: 3 }}>
        {isLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!isLoading && !error && interactions.length === 0 && (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6}>
            <NotesIcon sx={{ fontSize: 64, color: theme.palette.grey[300], mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No past interactions found
            </Typography>
          </Box>
        )}

        {!isLoading && !error && interactions.length > 0 && (
          <Timeline
            sx={{
              p: 0,
              m: 0,
              "& .MuiTimelineItem-root:before": {
                flex: 0,
                padding: 0
              }
            }}
          >
            {interactions.map((interaction, index) => (
              <TimelineItem key={interaction.id}>
                <TimelineOppositeContent
                  sx={{
                    flex: 0.3,
                    py: 2,
                    px: 2
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {interaction.interactionDate
                      ? formatDate(interaction.interactionDate)
                      : formatDate(interaction.createdAt)}
                  </Typography>
                </TimelineOppositeContent>

                <TimelineSeparator>
                  <TimelineDot
                    sx={{
                      bgcolor: alpha(getInteractionColor(interaction.type?.name || null), 0.1),
                      color: getInteractionColor(interaction.type?.name || null),
                      border: `2px solid ${getInteractionColor(interaction.type?.name || null)}`,
                      p: 1
                    }}
                  >
                    {getInteractionIcon(interaction.type?.name || null)}
                  </TimelineDot>
                  {index < interactions.length - 1 && <TimelineConnector />}
                </TimelineSeparator>

                <TimelineContent sx={{ py: 2, px: 2 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.grey[100], 0.5),
                      border: `1px solid ${theme.palette.divider}`
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Chip
                          label={interaction.type?.name || "Unknown"}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            bgcolor: alpha(getInteractionColor(interaction.type?.name || null), 0.1),
                            color: getInteractionColor(interaction.type?.name || null),
                            border: `1px solid ${alpha(getInteractionColor(interaction.type?.name || null), 0.3)}`
                          }}
                        />
                        {interaction.interactionCategory && (
                          <Chip
                            label={interaction.interactionCategory}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontWeight: 500,
                              color: theme.palette.text.secondary,
                              borderColor: theme.palette.divider
                            }}
                          />
                        )}
                      </Box>

                      {interaction.clientCultivator && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <PersonIcon sx={{ fontSize: "1rem", color: theme.palette.text.secondary }} />
                          <Typography variant="body2" color="text.secondary">
                            {interaction.clientCultivator.firstName} {interaction.clientCultivator.lastName}
                          </Typography>
                        </Box>
                      )}

                      {interaction.orderNumber && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <DescriptionIcon sx={{ fontSize: "1rem", color: theme.palette.text.secondary }} />
                          <Link
                            href={`${momentumUrl}new-home/${interaction.orderNumber}`}
                            target="_blank"
                            underline="hover"
                            sx={{ display: "inline-flex", alignItems: "center" }}
                          >
                            <Typography variant="body2" fontWeight={500}>
                              {interaction.orderNumber}
                            </Typography>
                          </Link>
                        </Box>
                      )}

                      {interaction.event?.name && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <EventIcon sx={{ fontSize: "1rem", color: theme.palette.text.secondary }} />
                          <Typography variant="body2" fontWeight={500}>
                            {interaction.event.name}
                          </Typography>
                        </Box>
                      )}

                      {interaction.notes && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Notes:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: "pre-wrap",
                              bgcolor: theme.palette.background.paper,
                              p: 1.5,
                              borderRadius: 1,
                              border: `1px solid ${theme.palette.divider}`
                            }}
                          >
                            {interaction.notes}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        )}
      </DialogContent>
    </Dialog>
  );
};
